import { Link, useNavigate } from "react-router-dom";
import { UserRound, ChevronDown, Package, UserCog, ShieldCheck, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

export const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";

export const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return <span className="h-8 w-8 rounded-full bg-slate-100 animate-pulse" aria-hidden="true" />;
  }
  if (!user) {
    return (
      <Button asChild variant="ghost" data-testid="nav-login-btn"
        className="rounded-full text-slate-700 hover:text-cyan-700 hover:bg-cyan-50 px-2 xl:px-3">
        <Link to="/cuenta">
          <UserRound className="h-4 w-4" />
          <span className="hidden xl:inline ml-1.5 text-sm">Mi cuenta</span>
        </Link>
      </Button>
    );
  }

  const firstName = user.name?.split(" ")[0] || "Cuenta";
  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="nav-user-menu"
          className="flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1 pr-2 py-1 hover:border-cyan-300 hover:shadow-sm transition-[border-color,box-shadow] duration-200 notranslate">
          <Avatar className="h-7 w-7">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs font-semibold">{initialsOf(user.name)}</AvatarFallback>
          </Avatar>
          <span className="hidden xl:inline text-sm font-medium text-slate-700 max-w-[110px] truncate">{firstName}</span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-xl p-1.5">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-semibold text-slate-900 truncate notranslate">{user.name}</p>
          <p className="text-xs text-slate-500 truncate notranslate">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="nav-my-orders" onClick={() => navigate("/cuenta/panel")} className="cursor-pointer rounded-lg">
          <Package className="h-4 w-4 mr-2 text-cyan-600" /> Mis pedidos
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="nav-my-profile" onClick={() => navigate("/cuenta/panel?tab=datos")} className="cursor-pointer rounded-lg">
          <UserCog className="h-4 w-4 mr-2 text-cyan-600" /> Mis datos
        </DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem data-testid="nav-admin-panel" onClick={() => navigate("/admin/panel")} className="cursor-pointer rounded-lg">
            <ShieldCheck className="h-4 w-4 mr-2 text-cyan-600" /> Panel de administración
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="nav-logout" onClick={doLogout} className="cursor-pointer rounded-lg text-red-600 focus:text-red-700">
          <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
